import React, { useState } from "react";
import {
  Badge,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import * as XLSX from "xlsx";

export interface ImportRowError {
  rowNumber: number;
  identifier: string;
  reason: string;
}
export interface ImportSummary {
  totalRows: number;
  successCount: number;
  failedCount: number;
  errors: ImportRowError[];
}

interface ReportImportModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  /** Executes the import; resolves with the summary. */
  onImport: (file: File) => Promise<ImportSummary>;
  isImporting?: boolean;
  /** Called after a run so the parent can refresh the report. */
  onImported?: () => void;
}

const ACCEPT = ".xlsx,.xls,.csv";

export function ReportImportModal({
  opened,
  onClose,
  title,
  onImport,
  isImporting,
  onImported,
}: ReportImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const reset = () => {
    setFile(null);
    setSummary(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const runImport = async () => {
    if (!file) return;
    try {
      const result = await onImport(file);
      setSummary(result);
      onImported?.();
      showNotification({
        title: "Import complete",
        message: `${result.successCount} added, ${result.failedCount} failed of ${result.totalRows}.`,
        color: result.failedCount === 0 ? "green" : "yellow",
      });
    } catch {
      /* error toast handled by the mutation */
    }
  };

  const downloadFailedRows = () => {
    if (!summary?.errors?.length) return;
    const rows = summary.errors.map((e) => ({
      Row: e.rowNumber,
      Identifier: e.identifier,
      Reason: e.reason,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Failed Rows");
    XLSX.writeFile(wb, "failed_rows.xlsx");
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={title} size="lg" centered>
      <Stack spacing="md">
        {!summary && (
          <>
            <Text size="sm" color="dimmed">
              Upload a filled template (.xlsx or .csv). Rows are matched to users
              by email; invalid rows are skipped and reported.
            </Text>
            <input
              type="file"
              accept={ACCEPT}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Group position="right">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={runImport} loading={isImporting} disabled={!file}>
                Upload & Import
              </Button>
            </Group>
          </>
        )}

        {summary && (
          <>
            <Group spacing="md">
              <Badge color="blue" size="lg" variant="light">
                Processed: {summary.totalRows}
              </Badge>
              <Badge color="green" size="lg" variant="light">
                Added: {summary.successCount}
              </Badge>
              <Badge color="red" size="lg" variant="light">
                Failed: {summary.failedCount}
              </Badge>
            </Group>

            {summary.errors.length > 0 && (
              <>
                <Text weight={600} size="sm">
                  Failed rows
                </Text>
                <ScrollArea h={280}>
                  <Table striped fontSize="xs">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Identifier</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.errors.map((e) => (
                        <tr key={`${e.rowNumber}-${e.identifier}`}>
                          <td>{e.rowNumber}</td>
                          <td>{e.identifier}</td>
                          <td>{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollArea>
              </>
            )}

            <Group position="apart">
              <Button
                variant="light"
                onClick={downloadFailedRows}
                disabled={summary.errors.length === 0}
              >
                Download failed rows
              </Button>
              <Group>
                <Button variant="default" onClick={reset}>
                  Import another file
                </Button>
                <Button onClick={handleClose}>Done</Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default ReportImportModal;
